import { ref, onMounted, onUnmounted } from 'vue';

/**
 * Composable para manejo optimizado de eventos de scroll
 * Previene problemas de rendimiento con scroll-linked positioning
 */
export function useOptimizedScroll() {
  const scrollY = ref(0);
  const scrollX = ref(0);
  const isScrolling = ref(false);
  
  let ticking = false;
  let scrollTimeout = null;
  let callbacks = new Set();
  
  // Función optimizada para manejar scroll
  const handleScroll = () => {
    const currentScrollY = window.scrollY;
    const currentScrollX = window.scrollX;
    
    // Actualizar valores inmediatamente para lecturas síncronas
    scrollY.value = currentScrollY;
    scrollX.value = currentScrollX;
    isScrolling.value = true;
    
    // Limpiar timeout anterior
    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
    }
    
    // Usar requestAnimationFrame para optimizar las actualizaciones del DOM
    if (!ticking) {
      requestAnimationFrame(() => {
        // Ejecutar todos los callbacks registrados
        callbacks.forEach(callback => {
          try {
            callback({
              scrollY: currentScrollY,
              scrollX: currentScrollX,
              deltaY: currentScrollY - scrollY.value,
              deltaX: currentScrollX - scrollX.value
            });
          } catch (error) {
            console.warn('Error en callback de scroll:', error);
          }
        });
        
        ticking = false;
      });
      ticking = true;
    }
    
    // Detectar fin de scroll
    scrollTimeout = setTimeout(() => {
      isScrolling.value = false;
    }, 150);
  };
  
  // Agregar listener de scroll con throttling
  const addScrollListener = (callback) => {
    if (typeof callback !== 'function') {
      console.warn('addScrollListener: callback debe ser una función');
      return;
    }
    
    callbacks.add(callback);
    
    // Retornar función para remover el listener
    return () => {
      callbacks.delete(callback);
    };
  };
  
  // Función específica para efectos de navbar
  const useNavbarScroll = (navbarElement, threshold = 10) => {
    if (!navbarElement) {
      console.warn('useNavbarScroll: elemento navbar requerido');
      return;
    }
    
    let lastState = null;
    
    const navbarCallback = ({ scrollY }) => {
      const shouldShow = scrollY > threshold;
      
      // Solo actualizar si el estado cambió
      if (lastState !== shouldShow) {
        if (shouldShow) {
          navbarElement.classList.add("blur", "position-sticky", "shadow-blur", "scrolled");
        } else {
          navbarElement.classList.remove("blur", "position-sticky", "shadow-blur", "scrolled");
        }
        lastState = shouldShow;
      }
    };
    
    return addScrollListener(navbarCallback);
  };
  
  // Función específica para reposicionamiento de tooltips
  const useTooltipScroll = (updatePositionFn, isVisible) => {
    if (typeof updatePositionFn !== 'function') {
      console.warn('useTooltipScroll: updatePositionFn debe ser una función');
      return;
    }
    
    const tooltipCallback = () => {
      if (isVisible.value) {
        updatePositionFn();
      }
    };
    
    return addScrollListener(tooltipCallback);
  };
  
  // Configurar listeners al montar
  onMounted(() => {
    // Usar passive listeners para mejor rendimiento
    window.addEventListener('scroll', handleScroll, { 
      passive: true, 
      capture: false 
    });
    
    // Inicializar valores
    scrollY.value = window.scrollY;
    scrollX.value = window.scrollX;
  });
  
  // Limpiar al desmontar
  onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
    callbacks.clear();
    
    if (scrollTimeout) {
      clearTimeout(scrollTimeout);
    }
  });
  
  return {
    // Estado reactivo
    scrollY,
    scrollX,
    isScrolling,
    
    // Métodos
    addScrollListener,
    useNavbarScroll,
    useTooltipScroll
  };
}

/**
 * Hook específico para detectar dirección de scroll
 */
export function useScrollDirection() {
  const direction = ref('up'); // 'up' | 'down'
  const { addScrollListener } = useOptimizedScroll();
  
  let lastScrollY = 0;
  
  const removeListener = addScrollListener(({ scrollY }) => {
    if (scrollY > lastScrollY && scrollY > 10) {
      direction.value = 'down';
    } else if (scrollY < lastScrollY) {
      direction.value = 'up';
    }
    lastScrollY = scrollY;
  });
  
  onUnmounted(() => {
    removeListener();
  });
  
  return {
    direction
  };
}

/**
 * Hook para hide/show navbar basado en scroll
 */
export function useHideOnScroll(threshold = 100) {
  const isHidden = ref(false);
  const { addScrollListener } = useOptimizedScroll();
  
  let lastScrollY = 0;
  
  const removeListener = addScrollListener(({ scrollY }) => {
    // Solo ocultar si hay suficiente scroll
    if (scrollY < threshold) {
      isHidden.value = false;
      return;
    }
    
    // Ocultar al hacer scroll hacia abajo
    if (scrollY > lastScrollY && scrollY > threshold) {
      isHidden.value = true;
    } 
    // Mostrar al hacer scroll hacia arriba
    else if (scrollY < lastScrollY - 5) {
      isHidden.value = false;
    }
    
    lastScrollY = scrollY;
  });
  
  onUnmounted(() => {
    removeListener();
  });
  
  return {
    isHidden
  };
}